import {
  DataProvider,
  CrudFilters,
  CrudSorting,
  Pagination,
} from "@refinedev/core";
import axios, { AxiosInstance } from "axios";
import { ENV } from "../config/env";


/**
 * Configured axios instance with baseURL set to API endpoint.
 *
 * For direct API calls outside of dataProvider, use relative paths WITHOUT leading slash:
 * - axiosInstance.post("workflow/complete", {...})  ✓ correct
 * - axiosInstance.post("/api/workflow/complete", {...})  ✗ wrong (double /api)
 *
 * The baseURL (ENV.API_URL, default "/api") is automatically prepended.
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: ENV.API_URL,
});

/**
 * System resources that have their own dedicated endpoints (not item types).
 * All Aras ItemTypes use the generic /items/{itemType} endpoint.
 */
const SYSTEM_RESOURCES = new Set([
  "auth",
  "users",
  "metadata",
  "health",
  "workflow", // InBasket workflow operations
]);

/**
 * Get the API path for a resource.
 * System resources use their own endpoints.
 * All item types use /items/{itemType} with EXACT Aras ItemType name.
 */
const getResourcePath = (resource: string): string => {
  if (SYSTEM_RESOURCES.has(resource)) {
    return resource;
  }
  // All item types use generic items endpoint with exact name
  return `items/${resource}`;
};

// Add auth token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 and extract error messages from response
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || "";
    const isAuthEndpoint =
      requestUrl.includes("/auth/") || requestUrl.startsWith("auth/");

    // Only redirect on 401 for non-auth endpoints (let login handle its own errors)
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/login";
    }

    // Extract error message from response body if available
    // Backend returns: { error: "...", message: "..." }
    // We want to show the detailed message to the user instead of generic "Request failed with status code XXX"
    if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }

    return Promise.reject(error);
  },
);

const buildFilters = (filters?: CrudFilters): Record<string, string> => {
  const params: Record<string, string> = {};

  if (!filters) return params;

  filters.forEach((filter) => {
    if (
      "field" in filter &&
      filter.value !== undefined &&
      filter.value !== null &&
      filter.value !== ""
    ) {
      // For "contains" operator on column filters, wrap with % for LIKE matching
      // Exclude "search" field as the API already wraps it with % internally
      if (filter.operator === "contains" && filter.field !== "search") {
        params[filter.field] = `%${filter.value}%`;
      } else {
        params[filter.field] = String(filter.value);
      }
    }
  });

  return params;
};

const buildSorting = (
  sorting?: CrudSorting,
): { orderBy?: string; orderDesc?: boolean } => {
  if (!sorting || sorting.length === 0) return {};

  const sort = sorting[0];
  return {
    orderBy: sort.field,
    orderDesc: sort.order === "desc",
  };
};

const buildPagination = (
  pagination?: Pagination,
): { page?: number; pageSize?: number } => {
  if (!pagination) return {};

  return {
    page: pagination.current,
    pageSize: pagination.pageSize,
  };
};

const SITE_SCOPING_ENABLED = ENV.SITE_SCOPING_ENABLED;

/**
 * Get site filter from sessionStorage (set by SiteSelector for admin users).
 * Returns siteId if set, otherwise undefined (backend will use user's session siteId).
 */
const getSiteFilter = (): string | undefined => {
  if (!SITE_SCOPING_ENABLED) return undefined;
  const siteId = sessionStorage.getItem("site_filter");
  return siteId || undefined;
};

/**
 * Clean variables before sending to API.
 * Removes undefined, null, and empty string values.
 */
const cleanVariables = (
  variables: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(variables)) {
    if (value === undefined || value === null || value === "") continue;
    result[key] = value;
  }

  return result;
};

/**
 * Data provider for Refine.
 *
 * Note: The apiUrl parameter is kept for Refine compatibility but is NOT used internally.
 * All API calls use axiosInstance which has baseURL already configured from ENV.API_URL.
 * This ensures consistent API endpoint configuration from a single source (ENV).
 */
export const dataProvider = (_apiUrl: string): DataProvider => ({
  getList: async ({ resource, pagination, filters, sorters }) => {
    const resourcePath = getResourcePath(resource);

    // Get site filter from sessionStorage (admin can override)
    const siteId = getSiteFilter();

    const queryParams = {
      ...buildFilters(filters),
      ...buildSorting(sorters),
      ...buildPagination(pagination),
      ...(siteId ? { siteId } : {}),
    };

    const { data } = await axiosInstance.get(resourcePath, {
      params: queryParams,
    });

    return {
      data: data.data || data,
      total: data.total || data.length,
    };
  },

  getOne: async ({ resource, id }) => {
    const resourcePath = getResourcePath(resource);
    const { data } = await axiosInstance.get(`${resourcePath}/${id}`);
    return { data };
  },

  create: async ({ resource, variables }) => {
    const resourcePath = getResourcePath(resource);

    // Extract _relationships from variables (if present)
    const { _relationships, ...itemProperties } = variables as Record<
      string,
      unknown
    >;

    // Clean variables (remove empty values)
    const cleanedVariables = cleanVariables(itemProperties);

    // Build payload with relationships if present
    const payload: Record<string, unknown> = cleanedVariables;
    if (_relationships && Object.keys(_relationships as object).length > 0) {
      payload.relationships = _relationships;
    }

    const { data } = await axiosInstance.post(resourcePath, payload);
    return { data };
  },

  update: async ({ resource, id, variables }) => {
    const resourcePath = getResourcePath(resource);
    const { data } = await axiosInstance.put(
      `${resourcePath}/${id}`,
      variables,
    );
    return { data };
  },

  deleteOne: async ({ resource, id }) => {
    const resourcePath = getResourcePath(resource);
    const { data } = await axiosInstance.delete(`${resourcePath}/${id}`);
    return { data };
  },

  getMany: async ({ resource, ids }) => {
    const resourcePath = getResourcePath(resource);
    const promises = ids.map((id) =>
      axiosInstance.get(`${resourcePath}/${id}`),
    );
    const responses = await Promise.all(promises);
    return { data: responses.map((response) => response.data) };
  },

  getApiUrl: () => ENV.API_URL,

  custom: async ({ url, method, payload, query, headers }) => {
    // Remove leading slash if present (baseURL already has the prefix)
    const cleanUrl = url.startsWith("/") ? url.slice(1) : url;
    let requestUrl = cleanUrl;

    if (query) {
      const queryString = new URLSearchParams(
        query as Record<string, string>,
      ).toString();
      requestUrl = `${requestUrl}?${queryString}`;
    }

    const response = await axiosInstance({
      method: method as string,
      url: requestUrl,
      data: payload,
      headers,
    });

    return { data: response.data };
  },
});

export { axiosInstance };
